import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, Subject, fromEvent, BehaviorSubject, combineLatest } from 'rxjs';
import { takeUntil, take, debounceTime, map, startWith, tap } from 'rxjs/operators';
import { FacilityService } from 'src/app/services/facility.service';
import { Location } from 'src/app/models/common-models';

@Component({
  selector: 'app-locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationsComponent implements OnInit, OnDestroy {
  locations$: Observable<Location[]>;
  selectedId: number | null = null;
  newestLocationId: number | null = null; // ID of the most recently opened location
  private destroy$ = new Subject<void>();
  private userLocation$ = new BehaviorSubject<{ lat: number; lng: number } | null>(null);
  
  // Responsive state - determine if mobile based on viewport width
  isMobile = false;

  // Mobile sheet state
  // Card Bottom (collapsed) - Google card at bottom, map takes most screen
  // Card Middle (half) - 50:50 split, DEFAULT
  // Card Top (expanded) - Card at top, content scrollable
  sheetState: 'collapsed' | 'half' | 'expanded' = 'half'; // Start at middle (50:50) as default
  private sheetStartY = 0;
  private sheetCurrentY = 0;
  private isDragging = false;
  private readonly SHEET_COLLAPSED_HEIGHT = 120; // Height when collapsed (just handle visible)
  private readonly SHEET_HALF_HEIGHT = 50; // 50% of screen for half state
  private readonly SHEET_EXPANDED_HEIGHT = 100; // Full screen when expanded (100vh)

  constructor(
    private facilityService: FacilityService,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Initialize mobile state
    if (isPlatformBrowser(this.platformId)) {
      this.updateMobileState();
      // Listen for window resize to update mobile state
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(100),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.updateMobileState();
          this.cdr.markForCheck();
        });
    }
  }

  private updateMobileState(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth <= 991;
    }
  }

  ngOnInit(): void {
    console.log('[LocationsPage] ngOnInit, isMobile:', this.isMobile);
    
    // Request geolocation permission and get user's location
    this.requestUserLocation();
    
    // Load locations from FacilityService and combine with user location to sort by distance
    // This will reactively update when user location becomes available
    this.locations$ = combineLatest([
      this.loadLocations(),
      this.userLocation$.pipe(startWith(null))
    ]).pipe(
      tap(([locations, userLocation]) => {
        // Set newestLocationId before mapping - use tap to set it synchronously
        // First, sort by OpeningDate descending to identify the newest location
        const sortedByDate = [...locations].sort((a, b) => {
          const dateA = a.OpeningDate ? new Date(a.OpeningDate).getTime() : 0;
          const dateB = b.OpeningDate ? new Date(b.OpeningDate).getTime() : 0;
          return dateB - dateA; // Descending order (newest first)
        });
        
        // Identify the most recently opened location (first in sorted list, and must be in the past)
        if (sortedByDate.length > 0) {
          const newest = sortedByDate[0];
          const now = new Date();
          const newestDate = newest.OpeningDate ? new Date(newest.OpeningDate) : null;
          // Only mark as "new" if it's opened in the past (not coming soon)
          if (newestDate && newestDate <= now) {
            this.newestLocationId = newest.Id;
            console.log('[LocationsPage] Newest location identified:', {
              id: newest.Id,
              name: newest.Name,
              openingDate: newest.OpeningDate,
              newestLocationId: this.newestLocationId
            });
          } else {
            this.newestLocationId = null;
            console.log('[LocationsPage] No newest location (all are coming soon or no dates)');
          }
        } else {
          this.newestLocationId = null;
        }
        
        // Trigger change detection to update child components
        this.cdr.markForCheck();
      }),
      map(([locations, userLocation]) => {
        if (userLocation) {
          // Calculate distances and sort
          const locationsWithDistance = locations.map(loc => {
            const distance = this.calculateDistance(
              userLocation.lat,
              userLocation.lng,
              loc.Latitude,
              loc.Longitude
            );
            return { ...loc, distance };
          });
          
          // Sort by distance (closest first)
          locationsWithDistance.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
          
          console.log('[LocationsPage] Locations sorted by distance:', locationsWithDistance.map(l => ({
            name: l.Name,
            distance: (l as any).distance?.toFixed(2) + ' miles'
          })));
          
          return locationsWithDistance;
        }
        // If no user location, return original order
        return locations;
      })
    );
    
    // Auto-select closest location (or first if no geolocation)
    // This will trigger when locations are first loaded, and again when sorted by distance
    let initialSelectionDone = false;
    this.locations$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(locations => {
      console.log('[LocationsPage] Locations loaded:', locations.length);
      const userLocation = this.userLocation$.value;
      
      if (locations.length > 0 && !this.selectedId && !initialSelectionDone) {
        // Select the closest location (first in sorted array) or first location
        this.selectedId = locations[0].Id;
        console.log('[LocationsPage] Auto-selected location:', this.selectedId);
        initialSelectionDone = true;
        this.cdr.markForCheck();
      } else if (locations.length > 0 && initialSelectionDone && userLocation) {
        // If user location becomes available after initial load, update selection to closest
        const closestId = locations[0].Id;
        if (closestId !== this.selectedId) {
          console.log('[LocationsPage] Updating selection to closest location:', closestId);
          this.selectedId = closestId;
          this.cdr.markForCheck();
        }
      }
    });
  }

  /**
   * Load locations from FacilityService
   */
  private loadLocations(): Observable<Location[]> {
    return new Observable(observer => {
      this.facilityService.getLocations()
        .then(locations => {
          observer.next(locations);
          observer.complete();
        })
        .catch(error => {
          console.error('[LocationsPage] Error loading locations:', error);
          observer.next([]);
          observer.complete();
        });
    });
  }

  /**
   * Request user's geolocation permission and get coordinates
   */
  private requestUserLocation(): void {
    if (!isPlatformBrowser(this.platformId)) {
      console.log('[LocationsPage] Not in browser, skipping geolocation');
      return;
    }

    if (!navigator.geolocation) {
      console.log('[LocationsPage] Geolocation not supported');
      return;
    }

    console.log('[LocationsPage] Requesting geolocation permission...');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        console.log('[LocationsPage] User location obtained:', userLocation);
        this.userLocation$.next(userLocation);
        
        // Re-trigger location sorting when user location is available
        this.cdr.markForCheck();
      },
      (error) => {
        console.log('[LocationsPage] Geolocation error:', error.message);
        // User denied permission or error occurred - continue without sorting
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0 // Don't use cached position
      }
    );
  }

  /**
   * Calculate distance between two coordinates using Haversine formula
   * Returns distance in miles
   */
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   */
  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackById = (_: number, item: Location) => item.Id;

  onSelect(id: number | null) {
    this.selectedId = id || null;
    this.cdr.markForCheck();
  }

  // Check if touch is on Google Maps card/info window
  private isGoogleMapsCard(element: HTMLElement): boolean {
    if (!element) return false;
    
    // Check if it's our custom mobile modal or sheet - definitely not Google Maps
    if (element.closest('.mobile-location-modal, .mobile-sheet')) {
      return false;
    }
    
    // Check if element is within Google Maps container
    const mapContainer = element.closest('.locations-map, .map-container');
    if (!mapContainer) return false;
    
    // Google Maps info windows are typically in elements with 'gm-' prefixed classes
    // or have specific Google Maps styling
    const isInGoogleMapsPane = element.closest('[class*="gm-"], [id*="gm-"]');
    if (isInGoogleMapsPane) {
      return true;
    }
    
    // Check if parent has Google Maps classes (info windows are nested)
    let parent = element.parentElement;
    let depth = 0;
    while (parent && depth < 5) { // Check up to 5 levels up
      if (parent.classList && Array.from(parent.classList).some(cls => cls.startsWith('gm-'))) {
        return true;
      }
      parent = parent.parentElement;
      depth++;
    }
    
    // Check if it's a scrollable element within the map (likely the info window content)
    const computedStyle = window.getComputedStyle(element);
    const isScrollable = computedStyle.overflow === 'auto' || 
                        computedStyle.overflow === 'scroll' || 
                        computedStyle.overflowY === 'auto' || 
                        computedStyle.overflowY === 'scroll';
    
    if (isScrollable && mapContainer) {
      // If it's scrollable and within the map (and not our custom components), it's likely the Google Maps card
      return true;
    }
    
    return false;
  }

  // Mobile sheet drag handlers
  onSheetTouchStart(event: TouchEvent): void {
    // Check if touch is on Google Maps card/info window - allow natural scrolling
    const target = event.target as HTMLElement;
    if (this.isGoogleMapsCard(target)) {
      return; // Don't interfere with Google Maps card scrolling
    }
    
    // Only start dragging if touch is on the sheet handle
    // Allow scrolling on sheet content
    const isOnHandle = target.closest('.sheet-handle');
    if (!isOnHandle) {
      return; // Allow natural scrolling on sheet content
    }
    
    this.isDragging = true;
    this.sheetStartY = event.touches[0].clientY;
    event.preventDefault();
  }

  onSheetTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;
    
    // Check if touch is on Google Maps card/info window - allow natural scrolling
    const target = event.target as HTMLElement;
    if (this.isGoogleMapsCard(target)) {
      this.isDragging = false; // Cancel drag if on Google Maps card
      return;
    }
    
    // Only handle drag if still on the handle
    const isOnHandle = target.closest('.sheet-handle');
    if (!isOnHandle) {
      this.isDragging = false; // Cancel drag if moved off handle
      return;
    }
    
    this.sheetCurrentY = event.touches[0].clientY;
    const deltaY = this.sheetCurrentY - this.sheetStartY;
    const sheetElement = (event.target as HTMLElement).closest('.mobile-sheet') as HTMLElement;
    
    if (sheetElement) {
      // Get current transform value based on state
      // Sheet is positioned at bottom: 0, with height: 100vh
      // Expanded: translateY(0) - no transform, card at top (full screen)
      // Half: translateY(50vh) - move down by 50vh, card on bottom 50vh, map on top 50vh
      // Collapsed: translateY(calc(100vh - 120px)) - move down to show only bottom 120px
      
      const viewportHeight = window.innerHeight;
      const halfHeight = viewportHeight * 0.5;
      const collapsedHeight = this.SHEET_COLLAPSED_HEIGHT;
      
      let currentTransform: number; // Transform value in pixels (positive = down, negative = up)
      if (this.sheetState === 'expanded') {
        currentTransform = 0; // No transform, card at top
      } else if (this.sheetState === 'half') {
        currentTransform = halfHeight; // Move down by 50vh (card on bottom, map on top)
      } else {
        // Collapsed: move down to show only collapsedHeight
        currentTransform = viewportHeight - collapsedHeight;
      }
      
      // Calculate new transform with drag (deltaY positive = dragging down, negative = dragging up)
      // When dragging down (deltaY > 0), we want to show more card (decrease transform)
      // When dragging up (deltaY < 0), we want to show less card (increase transform)
      const newTransform = currentTransform - deltaY;
      
      // Clamp between min (expanded) and max (collapsed)
      const minTransform = 0; // Expanded (no transform, card at top)
      const maxTransform = viewportHeight - collapsedHeight; // Collapsed (card at bottom, only 120px visible)
      const clampedTransform = Math.max(minTransform, Math.min(maxTransform, newTransform));
      
      // Apply transform - positive values move down, showing less card
      sheetElement.style.transform = `translateY(${clampedTransform}px)`;
      sheetElement.style.transition = 'none'; // Disable transition during drag
      
      // Update border radius based on position (smooth transition)
      const progress = (clampedTransform - minTransform) / (maxTransform - minTransform);
      const borderRadius = Math.max(0, 20 * (1 - progress));
      sheetElement.style.borderRadius = `${borderRadius}px ${borderRadius}px 0 0`;
      
      // Prevent default to allow smooth dragging
      event.preventDefault();
    }
  }

  onSheetTouchEnd(event: TouchEvent): void {
    if (!this.isDragging) return;
    
    const deltaY = this.sheetCurrentY - this.sheetStartY;
    const threshold = 30; // Minimum drag distance to trigger state change
    const velocity = Math.abs(deltaY) / 300; // Simple velocity calculation
    const sheetElement = (event.target as HTMLElement).closest('.mobile-sheet') as HTMLElement;
    
    // Calculate transform values (positive = down, negative = up)
    const viewportHeight = window.innerHeight;
    const halfHeight = viewportHeight * 0.5;
    const collapsedHeight = this.SHEET_COLLAPSED_HEIGHT;
    
    let currentTransform: number;
    if (this.sheetState === 'expanded') {
      currentTransform = 0; // No transform, card at top
    } else if (this.sheetState === 'half') {
      currentTransform = halfHeight; // Move down by 50vh (card on bottom, map on top)
    } else {
      currentTransform = viewportHeight - collapsedHeight; // Collapsed (card at bottom, only 120px visible)
    }
    
    // When dragging down (deltaY > 0), show more card (decrease transform)
    // When dragging up (deltaY < 0), show less card (increase transform)
    const finalTransform = currentTransform - deltaY;
    
    // Transform values for state determination
    // Transform of 0 = expanded (card at top), halfHeight = half (50:50), (viewportHeight - collapsedHeight) = collapsed
    const expandedPosition = 0;
    const halfPosition = halfHeight;
    const collapsedPosition = viewportHeight - collapsedHeight;
    
    // Determine target state based on final transform and drag direction
    if (Math.abs(deltaY) > threshold || velocity > 0.3) {
      if (deltaY > 0) {
        // Dragging down (transform decreases = showing more card)
        if (this.sheetState === 'expanded') {
          // From expanded, go to half or collapsed
          this.sheetState = finalTransform > halfPosition ? 'collapsed' : 'half';
        } else if (this.sheetState === 'half') {
          // From half, go to collapsed
          this.sheetState = 'collapsed';
        }
      } else {
        // Dragging up (transform increases = showing less card)
        if (this.sheetState === 'collapsed') {
          // From collapsed, go to half or expanded
          this.sheetState = finalTransform < halfPosition ? 'expanded' : 'half';
        } else if (this.sheetState === 'half') {
          // From half, go to expanded
          this.sheetState = 'expanded';
        }
      }
    } else {
      // Small drag - snap to nearest state
      const distanceToCollapsed = Math.abs(finalTransform - collapsedPosition);
      const distanceToHalf = Math.abs(finalTransform - halfPosition);
      const distanceToExpanded = Math.abs(finalTransform - expandedPosition);
      
      if (distanceToCollapsed < distanceToHalf && distanceToCollapsed < distanceToExpanded) {
        this.sheetState = 'collapsed';
      } else if (distanceToHalf < distanceToExpanded) {
        this.sheetState = 'half';
      } else {
        this.sheetState = 'expanded';
      }
    }
    
    // Re-enable transition and reset transform/border radius
    if (sheetElement) {
      sheetElement.style.transition = ''; // Re-enable transition
      sheetElement.style.transform = ''; // Reset to CSS class state
      sheetElement.style.borderRadius = ''; // Reset border radius
    }
    
    this.isDragging = false;
    this.sheetStartY = 0;
    this.sheetCurrentY = 0;
    this.cdr.markForCheck();
    event.preventDefault();
  }

  // Handle click on handle to cycle through states
  @HostListener('click', ['$event'])
  onHandleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.sheet-handle') && !this.isDragging) {
      // Cycle: collapsed -> half -> expanded -> collapsed
      if (this.sheetState === 'collapsed') {
        this.sheetState = 'half';
      } else if (this.sheetState === 'half') {
        this.sheetState = 'expanded';
      } else {
        this.sheetState = 'collapsed';
      }
      this.cdr.markForCheck();
    }
  }
}
